import React, { FC, useState } from "react";
import { useRouter } from "next/router";
import {
  JobPost,
  CheckoutSession,
  Compensation,
  Order,
  OrderItem,
  PaymentIntent,
  Location,
  Extras,
  Remote,
  Sku,
  JobPostAnalytics,
  Organisation,
} from "@prisma/client";
import { FormProvider, useForm } from "react-hook-form";
import { BeatLoader } from "react-spinners";
import { JobPostPublish } from "./components/JobPostPublish";
import { JobPostSummary } from "./components/JobPostSummary";
import { LocationSection } from "./sections/LocationSection";
import { ApplySection } from "./sections/ApplySection";
import { CompensationSection } from "./sections/CompensationSection";
import { HiringOrganisationSection } from "./sections/HiringOrganisationSection";
import { DescriptionSection } from "./sections/DescriptionSection";
import { NewUserSection } from "./sections/NewUserSection";
import { JobPostCompositeStatus } from "@utils/db/JobPost/getCompositeStatus";
import { getMoreMenuItems, MoreMenu } from "./MoreMenu";
import { JobPostPurchaseHistory } from "./components/JobPostPurchaseHistory";
import { NewTag } from "types/tags";
import { mixpanelEvent } from "@services/mixpanel";
import { AnalyticsEvent } from "@utils/redis/analytics/AnalyticsEvent";

export enum JobPostEditorPhase {
  CREATE,
  EDIT,
}
interface Props {
  hasUser: boolean;
  phase: JobPostEditorPhase;
  availableSkus: Sku[];
  purchasedSkus: Sku[];
  purchasedOrderItems?: (OrderItem & {
    order: Order & {
      checkoutSession: CheckoutSession;
      paymentIntent: PaymentIntent;
    };
    sku: Sku;
  })[];
  jobPost?: JobPost & {
    compensation: Compensation;
    hiringOrganisation: Organisation;
    sourcingOrganisation: Organisation;
    extras: Extras;
    locations: Location[];
    remote: Remote;
    analytics: JobPostAnalytics;
  };
  tags: NewTag[];
  compositeStatus?: JobPostCompositeStatus;
  isSticky?: boolean;
  publishStartsAt?: Date;
  publishExpiresAt?: Date;
  stoppedAt?: Date;
}

export const JobPostEditor: FC<Props> = ({
  hasUser,
  phase,
  availableSkus,
  purchasedSkus,
  purchasedOrderItems,
  jobPost,
  tags,
  compositeStatus,
  isSticky,
  publishStartsAt,
  publishExpiresAt,
  stoppedAt,
}) => {
  const router = useRouter();

  // Do we allow a user to delete the job post?
  const isJobPostDeletable = purchasedSkus.length === 0;

  // Selected SKUs state (SKUs to include with the order)
  // Any sku.isRequiredForJobPost === true that has not yet been purchased will be inculded in initialSelectedSkuIds (i.e. can't buy a promo if no publish)
  const initialSelectedSkuIds = availableSkus
    .filter((sku) => sku.isRequiredForJobPost)
    .map((sku) => sku.id);
  const [selectedSkuIds, setSelectedSkuIds] = useState(initialSelectedSkuIds);

  // form
  const formMethods = useForm({
    defaultValues: {
      jobPost: {
        taggerAssignedTags: [],
        userRejectedTags: [],
        userAssignedTags: [],
        ...jobPost,
        // We don't need to pollute the form with analytics data.
        analytics: undefined,
      },
    },
  });
  const {
    handleSubmit,
    getValues,
    formState: { errors },
    setError,
  } = formMethods;

  // isSaving state
  const [isSaving, setIsSaving] = useState(false);

  // Method: Save.
  const save = async (data) => {
    setIsSaving(true);

    const url =
      phase === JobPostEditorPhase.CREATE
        ? "/api/job-posts/create"
        : `/api/job-posts/${jobPost.id}/edit`;

    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    const result = await res.json();

    if (result.status === "error") {
      // console.log(result.errors);
      for (const [key, value] of Object.entries(result.errors)) {
        // @ts-ignore:next-line
        setError(key, {
          type: (value as any).type as string,
          message: (value as any).message as string,
        });
      }
    }

    const jobPostId = result?.jobPost?.id;

    // If creating a new job post, change the url to become the edit url (we should now have the id).
    if (phase === JobPostEditorPhase.CREATE && hasUser && !!jobPostId) {
      const newUrl = window.location.href.replace(
        "/create",
        `/${jobPostId}/edit`
      );
      await router.push(newUrl);
    }

    setIsSaving(false);
    return jobPostId;
  };

  // Method: Save & Preview.
  const saveAndPreview = async (data) => {
    const jobPostId = await save(data);

    // Return early if no jobPostId.
    if (!jobPostId) {
      return;
    }

    // Preview.
    const modalQuery = {
      ...router.query,
      jobPostIdForModal: jobPostId,
    };
    const modalHref =
      router.route +
      Object.entries(modalQuery).reduce((acc, [key, value]) => {
        return acc + `${key}=${value}&`;
      }, "?");
    router.push(modalHref);
  };

  // Method: Checkout.
  const checkout = async (data) => {
    // Save job post first.
    const jobPostId = await save(data);

    // Create new order.
    const url = `/api/job-posts/${jobPostId}/order/create`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        includeSkus: selectedSkuIds,
      }),
    });
    const result = await res.json();

    // Mixpanel: Record checkout creation.
    mixpanelEvent({
      name: AnalyticsEvent.CHECKOUT_CREATE,
      payload: {
        jobPostId: jobPostId,
        includeSkus: selectedSkuIds,
        orderId: result?.order?.id,
      },
    });

    // Is the value of any selectedSku larger than 0?
    const isCheckoutRequired = selectedSkuIds
      .map((skuId) => {
        const sku = availableSkus.find((sku) => sku.id === skuId);
        return sku.priceAmount;
      })
      .some((priceAmount) => priceAmount > 0);

    // Redirect either to checkout or to success page (if checkout not needed).
    let newUrl = "";
    if (isCheckoutRequired) {
      // Redirect to checkout.
      newUrl = `/checkout/session/create?order-id=${result?.order?.id}`;
    } else {
      // Redirect to success page.
      newUrl = `/checkout/success?order-id=${result?.order?.id}`;
    }

    await router.push(newUrl);
  };

  return (
    <FormProvider {...formMethods}>
      {/* Header */}
      <div className="sticky top-16 -mt-8 z-20 bg-white">
        <div className="hidden justify-between items-center py-5 px-0 border-b sm:flex">
          {/* Left side */}
          <div className="flex">
            <h1 className="text-3xl font-extrabold tracking-tight">
              {phase === JobPostEditorPhase.CREATE
                ? "New job post"
                : "Job Post"}
            </h1>
          </div>

          {/* Right side */}
          <div className="flex">
            {hasUser && (
              <div className="text-sm">
                <button
                  type="button"
                  className="items-center ml-2 w-20 py-2 border border-transparent shadow-sm font-medium rounded-md text-white bg-black focus:outline-none"
                  onClick={handleSubmit(save)}
                >
                  {isSaving ? (
                    <div className="text-center">
                      <BeatLoader size={6} margin={3} color="white" />
                    </div>
                  ) : (
                    "Save"
                  )}
                </button>
                <button
                  type="button"
                  className="items-center ml-2 w-20 py-2 border border-transparent shadow-sm font-medium rounded-md text-black bg-transparent border-black hover:bg-black/10 focus:outline-none"
                  onClick={handleSubmit(saveAndPreview)}
                >
                  {isSaving ? (
                    <div className="text-center">
                      <BeatLoader size={6} margin={3} color="black" />
                    </div>
                  ) : (
                    "Preview"
                  )}
                </button>
                {/* Display More Menu whenever it has any items. */}
                {getMoreMenuItems({
                  jobPostId: jobPost?.id,
                  compositeStatus,
                  isJobPostDeletable,
                  router,
                }).length > 0 && (
                  <MoreMenu
                    jobPostId={jobPost?.id}
                    compositeStatus={compositeStatus}
                    isJobPostDeletable={isJobPostDeletable}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex-1 lg:flex items-stretch mt-4">
        <main className="flex-1 overflow-y-auto">
          {/* Primary column */}
          <section
            aria-labelledby="primary-heading"
            className="min-w-0 flex-1 h-full flex flex-col overflow-hidden lg:order-last"
          >
            <div className="sm:mr-4">
              <form onSubmit={handleSubmit(save)}>
                <div className="m-0.5 mb-6 py-2 shadow bg-white sm:rounded-md sm:overflow-hidden">
                  <DescriptionSection tags={tags} />
                  <CompensationSection tags={tags} />
                  <HiringOrganisationSection />
                  <LocationSection tags={tags} />
                  <ApplySection isLast={hasUser} />
                  {!hasUser && <NewUserSection isLast={true} />}
                </div>
              </form>
            </div>
          </section>
        </main>

        {/* Secondary column (hidden on smaller screens) */}
        <aside className="sm:w-96">
          {/* section */}
          {hasUser && (
            <div className="mb-6">
              <JobPostSummary
                jobPostId={jobPost?.id}
                compositeStatus={compositeStatus}
                isSticky={isSticky}
                createdAt={jobPost?.createdAt}
                publishStartsAt={publishStartsAt}
                publishExpiresAt={publishExpiresAt}
                stoppedAt={stoppedAt}
                analytics={jobPost?.analytics}
              />
            </div>
          )}

          {/* section */}
          {availableSkus.length > 0 && (
            <JobPostPublish
              selectedSkuIds={selectedSkuIds}
              setSelectedSkuIds={setSelectedSkuIds}
              availableSkus={availableSkus}
              onSubmit={handleSubmit(checkout)}
            />
          )}

          {/* section */}
          {purchasedOrderItems?.length > 0 && (
            <div className="mb-6">
              <JobPostPurchaseHistory orderItems={purchasedOrderItems} />
            </div>
          )}
        </aside>
      </div>
      {/* <JobPostDetailModal /> */}
    </FormProvider>
  );
};
